import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setSelectedMedium,
  setSelectedClass,
  setSelectedSubject,
  setSelectedChapter,
  fetchChaptersBySubject,
} from "../redux/boardQuestionSlice";
import {
  fetchClassesByBoardMedium,
  fetchLinkedSubjects,
} from "../redux/chapterSlice";
import { fetchMediums } from "../redux/mediumSlice";

/**
 * Reusable cascading dropdown component for Board → Medium → Class → Subject → Chapter selection
 *
 * @param {string} boardId - Required board ID
 * @param {function} onSelectionChange - Optional callback when any selection changes
 * @param {boolean} showTitle - Whether to show the section title (default: true)
 * @param {string} containerClassName - Additional CSS classes for the container
 * @param {boolean} disabled - Whether to disable all dropdowns
 */
const BoardCascadingDropdowns = ({
  boardId,
  onSelectionChange,
  showTitle = true,
  containerClassName = "",
  disabled = false,
  layout = "",
}) => {
  const dispatch = useDispatch();

  // Redux state
  const mediums = useSelector((state) => state.mediums.list);
  const mediumsLoading = useSelector((state) => state.mediums.loading);

  const { classes, subjects, classesLoading, subjectsLoading } = useSelector(
    (state) => state.boardChapters,
  );

  const {
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
    chapters,
    chaptersLoading,
    disabledValue,
  } = useSelector((state) => state.boardQuestion);

  // Fetch mediums when component mounts
  useEffect(() => {
    if (boardId) {
      dispatch(fetchMediums(boardId));
    }
  }, [boardId, dispatch]);

  // Fetch classes when medium is selected
  useEffect(() => {
    if (selectedMedium && boardId) {
      dispatch(
        fetchClassesByBoardMedium({
          board_id: boardId,
          medium_id: selectedMedium,
        }),
      );
    }
  }, [selectedMedium, boardId, dispatch]);

  // Fetch subjects when class is selected
  useEffect(() => {
    if (selectedClass) {
      dispatch(fetchLinkedSubjects(selectedClass));
    }
  }, [selectedClass, dispatch]);

  // Fetch chapters when subject is selected
  useEffect(() => {
    if (selectedClass && selectedSubject) {
      dispatch(
        fetchChaptersBySubject({
          subject_id: selectedSubject,
          class_id: selectedClass,
        }),
      );
    }
  }, [selectedSubject, dispatch]);

  // Notify parent component of selection changes
  useEffect(() => {
    if (onSelectionChange) {
      onSelectionChange({
        mediumId: selectedMedium,
        classId: selectedClass,
        subjectId: selectedSubject,
        chapterId: selectedChapter,
      });
    }
  }, [
    selectedMedium,
    selectedClass,
    selectedSubject,
    selectedChapter,
    onSelectionChange,
  ]);

  const handleMediumChange = (e) => {
    dispatch(setSelectedMedium(e.target.value));
  };

  const handleClassChange = (e) => {
    dispatch(setSelectedClass(e.target.value));
  };

  const handleSubjectChange = (e) => {
    dispatch(setSelectedSubject(e.target.value));
  };

  const handleChapterChange = (e) => {
    dispatch(setSelectedChapter(e.target.value));
  };

  return (
    <>
      <style>
        {`
          .cascading-dropdown-container {
            background: #f8f9fa;
            border-radius: 8px;
            padding: 18px;
            margin-bottom: 18px;
            border: 1px solid #e0e0e0;
          }

          .cascading-dropdown-container .section-title {
            font-size: 15px;
            font-weight: 600;
            color: #2c3e50;
            margin-bottom: 15px;
            padding-bottom: 8px;
            border-bottom: 2px solid #3498db;
          }

          .cascading-label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 6px;
            display: block;
            font-size: 13px;
          }

          .cascading-select {
            width: 100%;
            padding: 8px 12px;
            border: 1px solid #d1d5db;
            border-radius: 6px;
            font-size: 14px;
            transition: all 0.2s ease;
            background: white;
            height: 38px;
          }

          .cascading-select:focus {
            outline: none;
            border-color: #3498db;
            box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
          }

          .cascading-select:disabled {
            background-color: #f5f5f5;
            cursor: not-allowed;
          }

          .required-star {
            color: #e74c3c;
            font-weight: 600;
          }
        `}
      </style>

      {layout === "" ? (
        <div className={`cascading-dropdown-container ${containerClassName}`}>
          {showTitle && (
            <div className="section-title">Select Question Location</div>
          )}

          {/* Medium and Class Row */}
          <div className="row" style={{ marginBottom: "12px" }}>
            {/* Medium */}
            <div className="col-sm-6" style={{ marginBottom: "0" }}>
              <label className="cascading-label">
                Medium <span className="required-star">*</span>
              </label>
              <select
                value={selectedMedium || ""}
                onChange={handleMediumChange}
                disabled={mediumsLoading || disabled || disabledValue}
                className="cascading-select"
              >
                <option value="">
                  {mediumsLoading ? "Loading..." : "-- Select Medium --"}
                </option>
                {mediums.map((medium) => (
                  <option key={medium.id} value={medium.id}>
                    {medium.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Class */}
            {selectedMedium && (
              <div className="col-sm-6" style={{ marginBottom: "0" }}>
                <label className="cascading-label">
                  Class <span className="required-star">*</span>
                </label>
                <select
                  value={selectedClass || ""}
                  onChange={handleClassChange}
                  disabled={classesLoading || disabled || disabledValue}
                  className="cascading-select"
                >
                  <option value="">
                    {classesLoading ? "Loading..." : "-- Select Class --"}
                  </option>
                  {classes.map((classItem) => (
                    <option key={classItem.class_id} value={classItem.class_id}>
                      {classItem.class_name}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {/* Subject and Chapter Row */}
          {selectedClass && (
            <div className="row" style={{ marginBottom: "0" }}>
              {/* Subject */}
              <div className="col-sm-6" style={{ marginBottom: "0" }}>
                <label className="cascading-label">
                  Subject <span className="required-star">*</span>
                </label>
                <select
                  value={selectedSubject || ""}
                  onChange={handleSubjectChange}
                  disabled={subjectsLoading || disabled || disabledValue}
                  className="cascading-select"
                >
                  <option value="">
                    {subjectsLoading ? "Loading..." : "-- Select Subject --"}
                  </option>
                  {subjects.map((subject) => (
                    <option key={subject.id} value={subject.id}>
                      {subject.subject_name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Chapter */}
              {selectedSubject && (
                <div className="col-sm-6" style={{ marginBottom: "0" }}>
                  <label className="cascading-label">
                    Chapter <span className="required-star">*</span>
                  </label>
                  <select
                    value={selectedChapter || ""}
                    onChange={handleChapterChange}
                    disabled={chaptersLoading || disabled || disabledValue}
                    className="cascading-select"
                  >
                    <option value="">
                      {chaptersLoading ? "Loading..." : "-- Select Chapter --"}
                    </option>
                    {chapters.map((chapter) => (
                      <option
                        key={chapter.chapter_id}
                        value={chapter.chapter_id}
                      >
                        {chapter.chapter_name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div style={{ display: "flex", gap: "2%" }}>
          {/* Medium and Class Row */}
          <div className="row" style={{ marginBottom: "12px" }}>
            {/* Medium */}
            <div className="col-sm-6" style={{ marginBottom: "0" }}>
              <label className="cascading-label">
                Medium
                {/* <span className="required-star">*</span> */}
              </label>
              <select
                value={selectedMedium || ""}
                onChange={handleMediumChange}
                disabled={mediumsLoading || disabled}
                className="cascading-select"
              >
                <option value="">
                  {mediumsLoading ? "Loading..." : "-- Select Medium --"}
                </option>
                {mediums.map((medium) => (
                  <option key={medium.id} value={medium.id}>
                    {medium.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Class */}

            <div className="col-sm-6" style={{ marginBottom: "0" }}>
              <label className="cascading-label">
                Class
                {/* <span className="required-star">*</span> */}
              </label>
              <select
                value={selectedClass || ""}
                onChange={handleClassChange}
                disabled={classesLoading || disabled}
                className="cascading-select"
              >
                <option value="">
                  {classesLoading ? "Loading..." : "-- Select Class --"}
                </option>
                {classes.map((classItem) => (
                  <option key={classItem.class_id} value={classItem.class_id}>
                    {classItem.class_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          {/* Subject and Chapter Row */}

          <div className="row" style={{ marginBottom: "0" }}>
            {/* Subject */}
            <div className="col-sm-6" style={{ marginBottom: "0" }}>
              <label className="cascading-label">
                Subject
                {/* <span className="required-star">*</span> */}
              </label>
              <select
                value={selectedSubject || ""}
                onChange={handleSubjectChange}
                disabled={subjectsLoading || disabled}
                className="cascading-select"
              >
                <option value="">
                  {subjectsLoading ? "Loading..." : "-- Select Subject --"}
                </option>
                {subjects.map((subject) => (
                  <option key={subject.id} value={subject.id}>
                    {subject.subject_name}
                  </option>
                ))}
              </select>
            </div>

            {/* Chapter */}

            <div className="col-sm-6" style={{ marginBottom: "0" }}>
              <label className="cascading-label">
                Chapter
                {/* <span className="required-star">*</span> */}
              </label>
              <select
                value={selectedChapter || ""}
                onChange={handleChapterChange}
                disabled={chaptersLoading || disabled}
                className="cascading-select"
              >
                <option value="">
                  {chaptersLoading ? "Loading..." : "-- Select Chapter --"}
                </option>
                {chapters.map((chapter) => (
                  <option key={chapter.chapter_id} value={chapter.chapter_id}>
                    {chapter.chapter_name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BoardCascadingDropdowns;
